import React, { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { InlineMath, BlockMath } from "react-katex"
import "katex/dist/katex.min.css"
import { Loader2, Plus, Pencil, Trash, Share2, Upload, ImageIcon, CheckSquare, FileText, List } from "lucide-react"
import { putAws } from "../putAws"
import { getAssignmentData } from "./api"
import jsPDF from "jspdf"

export default function EditCreatedAssignment() {
  const { assignmentId } = useParams()
  const navigate = useNavigate()
  const [assignment, setAssignment] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingIndex, setEditingIndex] = useState(-1)
  const [editedQuestion, setEditedQuestion] = useState({})
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [uploadedFile, setUploadedFile] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [deleteConfirmation, setDeleteConfirmation] = useState({ isOpen: false, index: -1 })

  useEffect(() => {
    const fetchAssignment = async () => {
      setIsLoading(true)
      try {
        const response = await getAssignmentData(assignmentId)
        setAssignment(response.body.data)
      } catch (err) {
        console.error("Error fetching assignment:", err)
        setError("Failed to load assignment. Please try again.")
      } finally {
        setIsLoading(false)
      }
    }

    if (assignmentId) {
      fetchAssignment()
    }
  }, [assignmentId])

  const getQuestionTypeIcon = (type) => {
    switch (type) {
      case "MCQ":
      case "Multiple-Choice":
        return <CheckSquare size={20} className="text-blue-500" />
      case "Short-Answer":
        return <FileText size={20} className="text-green-500" />
      case "Essay":
        return <FileText size={20} className="text-purple-500" />
      default:
        return <List size={20} className="text-gray-500" />
    }
  }

  const renderMathText = (text) => {
    if (!text) return null
    const parts = text.split(/(\$\$[\s\S]*?\$\$|\$[\s\S]*?\$)/)
    return parts.map((part, i) => {
      if (part.startsWith("$$") && part.endsWith("$$")) {
        return <BlockMath key={i}>{part.slice(2, -2)}</BlockMath>
      } else if (part.startsWith("$") && part.endsWith("$")) {
        return <InlineMath key={i}>{part.slice(1, -1)}</InlineMath>
      } else {
        return <span key={i}>{part}</span>
      }
    })
  }

  const handleShareAssignment = async () => {
    setIsShareModalOpen(false)
    setIsLoading(true)

    const assignmentData = {
      ...assignment,
      shareWithStudents: true,
    }

    try {
      const url = `https://api-dev.aurait.ai/assignment?id=${assignmentId}`
      const response = await putAws(url, assignmentData)

      if (response?.statusCode === 200) {
        setTimeout(() => {
          setIsLoading(false)
          navigate("/assignments")
        }, 2000)
      } else {
        throw new Error(response?.message || "Failed to update assignment")
      }
    } catch (error) {
      console.error("Error updating assignment:", error)
      setIsLoading(false)
      setError("Failed to share assignment. Please try again.")
    }
  }

  const handleSaveWithoutSharing = async () => {
    setIsLoading(true)

    const assignmentData = {
      ...assignment,
      shareWithStudents: assignment.shareWithStudents,
    }

    try {
      const url = `https://api-dev.aurait.ai/assignment?id=${assignmentId}`
      const response = await putAws(url, assignmentData)

      if (response?.statusCode === 200) {
        setTimeout(() => {
          setIsLoading(false)
          navigate("/assignments")
        }, 2000)
      } else {
        throw new Error(response?.message || "Failed to update assignment")
      }
    } catch (error) {
      console.error("Error updating assignment:", error)
      setIsLoading(false)
      setError("Failed to save assignment. Please try again.")
    }
  }

  const handleEdit = (index) => {
    const questionToEdit = assignment.assignmentQuestion[index]
    setEditingIndex(index)
    setEditedQuestion({
      ...questionToEdit,
      type: [questionToEdit.questionType],
    })
    setIsEditModalOpen(true)
  }

  const handleSaveEdit = () => {
    if (editingIndex !== -1) {
      setAssignment((prev) => ({
        ...prev,
        assignmentQuestion: prev.assignmentQuestion.map((q, i) =>
          i === editingIndex ? { ...editedQuestion, maxMarks: editedQuestion.maxMarks || 0 } : q,
        ),
      }))
    }
    setIsEditModalOpen(false)
    setEditingIndex(-1)
    setEditedQuestion({})
  }

  const handleDelete = (index) => {
    setDeleteConfirmation({ isOpen: true, index })
  }

  const confirmDelete = () => {
    setAssignment((prev) => ({
      ...prev,
      assignmentQuestion: prev.assignmentQuestion.filter((_, i) => i !== deleteConfirmation.index),
    }))
    setDeleteConfirmation({ isOpen: false, index: -1 })
  }

  const downloadAsPDF = () => {
    const doc = new jsPDF()
    let yOffset = 10

    // Add title
    doc.setFontSize(18)
    doc.text(assignment.assignment_topic, 10, yOffset)
    yOffset += 10

    // Add teacher name
    doc.setFontSize(12)
    doc.text(
      `Created by: ${JSON.parse(localStorage.getItem("teacherData"))?.teacherName?.split(" ")[0] || "Teacher"}`,
      10,
      yOffset,
    )
    yOffset += 10

    // Add questions
    assignment.assignmentQuestion.forEach((question, index) => {
      yOffset += 10
      if (yOffset > 280) {
        doc.addPage()
        yOffset = 10
      }
      doc.setFontSize(14)
      doc.text(`${index + 1}. ${question.question}`, 10, yOffset)
      yOffset += 7
      doc.setFontSize(10)
      doc.text(
        `Type: ${question.questionType} | Difficulty: ${question.questionDifficulty} | Max Marks: ${question.maxMarks}`,
        10,
        yOffset,
      )
      yOffset += 7

      if (question.questionType === "MCQ" || question.questionType === "Multiple-Choice") {
        question.mcqOptions.forEach((option, optionIndex) => {
          if (yOffset > 280) {
            doc.addPage()
            yOffset = 10
          }
          doc.text(`${String.fromCharCode(65 + optionIndex)}. ${option}`, 15, yOffset)
          yOffset += 7
        })
      }
    })

    doc.save(`${assignment.assignment_topic}.pdf`)
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <Loader2 className="animate-spin h-16 w-16 text-blue-500 mx-auto" />
          <p className="text-center mt-4">Loading assignment data...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="bg-white p-6 rounded-lg shadow-lg text-center">
          <Loader2 className="mx-auto text-red-500 mb-4" size={48} />
          <p className="text-xl font-semibold text-gray-800">{error}</p>
        </div>
      </div>
    )
  }

  if (!assignment) {
    return null
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8">
        <h1 className="text-3xl font-bold text-center text-gray-800 mb-8">
          {assignment.assignment_topic || "Edit Assignment"}
        </h1>

        <div className="flex justify-between mb-6">
          <button
            onClick={downloadAsPDF}
            className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
          >
            <Upload size={20} />
            <span>Download as PDF</span>
          </button>

          <button
            onClick={() => setIsShareModalOpen(true)}
            className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
          >
            <Share2 size={20} />
            <span>Share with Students</span>
          </button>
        </div>

        <div className="space-y-4">
          {assignment.assignmentQuestion &&
            assignment.assignmentQuestion.map((question, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-sm mb-4 border border-gray-200">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <h2 className="text-lg font-semibold text-gray-700 mb-2">Question {index + 1}</h2>
                    <p className="text-sm text-gray-500 mb-2">
                      Type: {question.questionType} | Difficulty: {question.questionDifficulty || "N/A"}
                    </p>
                    <div className="text-gray-600 whitespace-pre-wrap mb-4">{renderMathText(question.question)}</div>
                    {(question.questionType === "Multiple-Choice" || question.questionType === "MCQ") &&
                      question.mcqOptions && (
                        <div className="mt-2 space-y-2">
                          {question.mcqOptions.map((option, i) => (
                            <div key={i} className="flex items-center space-x-2">
                              <input
                                type="radio"
                                id={`q${index}-option${i}`}
                                name={`question-${index}`}
                                value={option}
                                className="form-radio h-4 w-4 text-indigo-600"
                              />
                              <label htmlFor={`q${index}-option${i}`} className="text-gray-700">
                                {renderMathText(option)}
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    <div className="flex items-center gap-2">
                      <label htmlFor={`marks-${index}`} className="text-sm text-gray-600">
                        Marks:
                      </label>
                      <input
                        id={`marks-${index}`}
                        type="number"
                        value={question.maxMarks || 0}
                        onChange={(e) => {
                          const newMarks = Number.parseInt(e.target.value, 10)
                          setAssignment((prev) => ({
                            ...prev,
                            assignmentQuestion: prev.assignmentQuestion.map((q, i) =>
                              i === index ? { ...q, maxMarks: newMarks } : q,
                            ),
                          }))
                        }}
                        className="w-16 px-2 py-1 border rounded text-right"
                        min="0"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(index)}
                        className="p-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                        aria-label="Edit question"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(index)}
                        className="p-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                        aria-label="Delete question"
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>

        <div className="mt-8 flex justify-between items-center">
          <p className="text-xl font-bold text-gray-800">
            Total Marks:{" "}
            {assignment.assignmentQuestion
              ? assignment.assignmentQuestion.reduce((total, q) => total + (q.maxMarks || 0), 0)
              : 0}
          </p>
          <button
            onClick={handleSaveWithoutSharing}
            className="px-6 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors flex items-center gap-2"
          >
            <Upload size={20} />
            Save without Sharing
          </button>
        </div>
      </div>

      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Edit Question</h2>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <input
                  type="text"
                  value={editedQuestion.questionType || ""}
                  readOnly
                  className="w-full p-1 border rounded text-sm bg-gray-100"
                />
              </div>
              <div>
                <label htmlFor="questionDifficulty" className="block text-sm font-medium text-gray-700 mb-1">
                  Difficulty
                </label>
                <select
                  id="questionDifficulty"
                  value={editedQuestion.questionDifficulty || "Easy"}
                  onChange={(e) => setEditedQuestion({ ...editedQuestion, questionDifficulty: e.target.value })}
                  className="w-full p-1 border rounded text-sm"
                >
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="Hard">Hard</option>
                </select>
              </div>
            </div>
            <div className="mb-3">
              <label htmlFor="questionText" className="block text-sm font-medium text-gray-700 mb-1">
                Question
              </label>
              <textarea
                id="questionText"
                value={editedQuestion.question || ""}
                onChange={(e) => setEditedQuestion({ ...editedQuestion, question: e.target.value })}
                className="w-full h-24 p-2 border rounded text-sm"
                placeholder="Enter question text"
              />
            </div>
            {(editedQuestion.questionType === "Multiple-Choice" || editedQuestion.questionType === "MCQ") && (
              <div className="mb-3">
                {(editedQuestion.mcqOptions || []).map((option, index) => (
                  <div key={index} className="flex mb-2">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => {
                        const newOptions = [...(editedQuestion.mcqOptions || [])]
                        newOptions[index] = e.target.value
                        setEditedQuestion({ ...editedQuestion, mcqOptions: newOptions })
                      }}
                      placeholder={`Option ${String.fromCharCode(97 + index)}`}
                      className="flex-grow p-1 border rounded text-sm"
                    />
                    <button
                      onClick={() => {
                        const newOptions = (editedQuestion.mcqOptions || []).filter((_, i) => i !== index)
                        setEditedQuestion({ ...editedQuestion, mcqOptions: newOptions })
                      }}
                      className="ml-2 px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                    >
                      -
                    </button>
                  </div>
                ))}
                {(editedQuestion.mcqOptions || []).length < 4 && (
                  <button
                    onClick={() => {
                      const newOptions = [...(editedQuestion.mcqOptions || []), ""]
                      setEditedQuestion({ ...editedQuestion, mcqOptions: newOptions })
                    }}
                    className="w-full px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                  >
                    Add Option
                  </button>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {isShareModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Share Assignment</h2>
            <p className="mb-4">
              Are you sure you want to share this assignment with students? This will set shareWithStudents to true.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsShareModalOpen(false)}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleShareAssignment}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
              >
                Confirm Share
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirmation.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Confirm Deletion</h2>
            <p className="mb-4">Are you sure you want to delete this question?</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteConfirmation({ isOpen: false, index: -1 })}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

